import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import localApiAdapter from '../../core/services/localApiAdapter';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  FileText,
  Filter,
  ChevronDown,
  ChevronUp,
  Upload,
  Download,
  FileSpreadsheet,
  Calendar,
  CheckCircle,
  XCircle,
  Plus,
  X,
  Package,
  Settings,
} from 'lucide-react';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import {
  DEFAULT_SAUDI_BONUSES,
  DEFAULT_SAUDI_ALLOWANCE_TYPES,
  mapSaudiPayrollTypeRow,
} from './utils/payrollSaudiFormat';
import { CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  SALAIRES_FORM_GRID_CLASS,
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  QawaemTableStickyActionsTd,
  QAWAEM_TABLE_ACTIONS_TH_CLASS,
  useSalairesTableSelection,
  renderSalairesBulkDeleteFilterAction,
} from './salairesListPageUtils';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';

const NS = 'modules.salaires.pages.primesIndemnitesPage';

const unwrapApiList = (response) => {
  if (Array.isArray(response)) return response;
  if (response?.success && Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const mapApiTypeRow = (item) => ({
  id: item.id,
  code: item.code,
  designation: item.designation,
  type: item.type === 'percentage' ? 'pourcentage' : 'fixe',
  valeurDefaut: item.default_value,
  plafond: item.ceiling,
  cotisable: item.is_cotisable === 1 || item.is_cotisable === true,
  imposable: item.is_taxable === 1 || item.is_taxable === true,
  description: item.description,
});

const toApiTypePayload = (formData) => ({
  code: formData.code,
  designation: formData.designation,
  type: formData.type === 'pourcentage' ? 'percentage' : 'fixed',
  default_value: formData.type === 'pourcentage'
    ? parseFloat(formData.valeurDefaut)
    : parseInt(formData.valeurDefaut, 10),
  ceiling: formData.plafond ? parseFloat(formData.plafond) : null,
  is_cotisable: !!formData.cotisable,
  is_taxable: !!formData.imposable,
  description: formData.description || '',
});

const MONTH_KEYS = ['jan_2026', 'feb_2026', 'mar_2026', 'apr_2026', 'may_2026', 'jun_2026'];

const PrimesIndemnitesPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);

  const seedSaudiBonuses = () =>
    DEFAULT_SAUDI_BONUSES.map((item) => mapSaudiPayrollTypeRow(item, t, NS));

  const seedSaudiAllowances = () =>
    DEFAULT_SAUDI_ALLOWANCE_TYPES.map((item) => mapSaudiPayrollTypeRow(item, t, NS));
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const [activeTab, setActiveTab] = useState('primes');
  const entityLabel = () => (activeTab === 'primes'
    ? p('tab_bonuses', { defaultValue: 'مكافأة' })
    : p('tab_allowances', { defaultValue: 'بدل' }));
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImportDropdownOpen, setIsImportDropdownOpen] = useState(false);
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setIsImportDropdownOpen(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setIsExportDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // État pour les primes et indemnités (fetched from API)
  const [primes, setPrimes] = useState([]);
  const [indemnites, setIndemnites] = useState([]);
  
  // Fetch data from API
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const [bonusRes, allowanceRes] = await Promise.all([
        localApiAdapter.get('/salaries/bonuses-allowances/bonus-types'),
        localApiAdapter.get('/salaries/bonuses-allowances/allowance-types'),
      ]);

      const bonusData = unwrapApiList(bonusRes);
      const allowanceData = unwrapApiList(allowanceRes);

      if (bonusData.length > 0) {
        setPrimes(sortRecordsNewestFirst(bonusData.map(mapApiTypeRow)));
      } else {
        setPrimes(seedSaudiBonuses());
      }

      if (allowanceData.length > 0) {
        setIndemnites(sortRecordsNewestFirst(allowanceData.map(mapApiTypeRow)));
      } else {
        setIndemnites(seedSaudiAllowances());
      }
    } catch (error) {
      console.error('Error fetching bonus/allowance types:', error);
      setPrimes(seedSaudiBonuses());
      setIndemnites(seedSaudiAllowances());
    } finally {
      setLoading(false);
    }
  };
  
  // État pour le nouvel élément en cours d'ajout
  const [formData, setFormData] = useState({
    code: '',
    designation: '',
    type: 'pourcentage',
    valeurDefaut: 0,
    plafond: '',
    cotisable: true,
    imposable: true,
    description: ''
  });
  
  // État pour l'élément en cours d'édition
  const [editingItem, setEditingItem] = useState(null);
  
  // Reset form
  const resetForm = () => {
    setShowModal(false);
    setEditingRecord(null);
    setFormData({
      code: '',
      designation: '',
      type: 'pourcentage',
      valeurDefaut: 0,
      plafond: '',
      cotisable: true,
      imposable: true,
      description: ''
    });
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    setFormData({
      code: '',
      designation: '',
      type: 'pourcentage',
      valeurDefaut: 0,
      plafond: '',
      cotisable: true,
      imposable: true,
      description: ''
    });
    setShowModal(true);
  };
  
  const filteredItems = useMemo(() => {
    const list = activeTab === 'primes' ? primes : indemnites;
    return list.filter((item) =>
      item.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [activeTab, primes, indemnites, searchTerm]);

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useSalairesTableSelection(filteredItems);

  const typeEndpoint = activeTab === 'primes' ? 'bonus-types' : 'allowance-types';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code?.trim() || !formData.designation?.trim()) {
      notifyError(showNotify, p('validation_required', { defaultValue: p('form_designation') }));
      return;
    }

    try {
      const payload = toApiTypePayload(formData);
      const basePath = `/salaries/bonuses-allowances/${typeEndpoint}`;

      if (editingRecord) {
        const result = await localApiAdapter.put(`${basePath}/${editingRecord.id}`, payload);
        if (result?.success === false) {
          notifyError(showNotify, result.error || result.message || p('save_error', { defaultValue: 'فشل الحفظ' }));
          return;
        }
        notifyUpdated(showNotify, entityLabel());
      } else {
        const result = await localApiAdapter.post(basePath, payload);
        if (result?.success === false) {
          notifyError(showNotify, result.error || result.message || p('save_error', { defaultValue: 'فشل الحفظ' }));
          return;
        }
        notifyCreated(showNotify, entityLabel());
      }

      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving bonus/allowance type:', error);
      notifyError(showNotify, error.message || p('save_error', { defaultValue: 'فشل الحفظ' }));
    }
  };
  
  // Handle edit
  const handleEdit = (item) => {
    setEditingRecord(item);
    setFormData({
      code: item.code,
      designation: item.designation,
      type: item.type,
      valeurDefaut: item.valeurDefaut,
      plafond: item.plafond || '',
      cotisable: item.cotisable,
      imposable: item.imposable,
      description: item.description || ''
    });
    setShowModal(true);
  };
  
  // Supprimer un élément
  const handleDeleteItem = async (id) => {
    if (!window.confirm(p('delete_confirm', { defaultValue: p('delete') }))) return;

    try {
      const basePath = `/salaries/bonuses-allowances/${typeEndpoint}`;
      const result = await localApiAdapter.delete(`${basePath}/${id}`);
      if (result?.success === false) {
        notifyError(showNotify, result.error || p('save_error', { defaultValue: 'فشل الحذف' }));
        return;
      }
      notifyDeleted(showNotify, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error deleting bonus/allowance type:', error);
      notifyError(showNotify, error.message || p('save_error', { defaultValue: 'فشل الحذف' }));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm_bulk', { count: selectedRecords.length, defaultValue: p('delete_confirm', { defaultValue: p('delete') }) }))) return;
    try {
      const basePath = `/salaries/bonuses-allowances/${typeEndpoint}`;
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${basePath}/${id}`)));
      clearSelection();
      notifyBulkDeleted(showNotify, deleteCount, entityLabel());
      await fetchData();
    } catch (error) {
      console.error('Error bulk deleting bonus/allowance types:', error);
      notifyError(showNotify, error.message || p('save_error', { defaultValue: 'فشل الحذف' }));
    }
  };

  // Calculate GLOBAL stats (combined for both tabs)
  const stats = {
    totalPrimes: primes.length,
    totalIndemnites: indemnites.length,
    cotisables: primes.filter(p => p.cotisable).length + indemnites.filter(i => i.cotisable).length,
    imposables: primes.filter(p => p.imposable).length + indemnites.filter(i => i.imposable).length,
    totalItems: primes.length + indemnites.length
  };

  const qawaemBaseColumns = useMemo(() => [
    { key: 'code', label: p('col_code'), required: true },
    { key: 'designation', label: p('col_designation'), required: true },
    { key: 'type', label: p('col_type') },
    { key: 'default_value', label: p('col_default_value') },
    { key: 'ceiling', label: p('col_ceiling') },
    { key: 'contributable', label: p('col_contributable') },
    { key: 'taxable', label: p('col_taxable') },
  ], [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: -1 }),
    [qawaemBaseColumns, showCol],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-white flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-blue-400" />
            {p('title')}
          </h2>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {renderSalairesBulkDeleteFilterAction({
            selectedCount: selectedRecords.length,
            onBulkDelete: handleBulkDelete,
            t,
          })}
          <button
            type="button"
            onClick={openCreateModal}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
          >
            <Plus size={20} />
            <span>{activeTab === 'primes' ? p('add_bonus') : p('add_allowance')}</span>
          </button>
        </div>
      </div>

      {/* 5 Stat Cards with Glow Effect - GLOBAL STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('kpi_total_bonuses'), value: stats.totalPrimes, icon: DollarSign, color: 'from-blue-500 to-blue-600' },
          { title: p('kpi_total_allowances'), value: stats.totalIndemnites, icon: DollarSign, color: 'from-green-500 to-green-600' },
          { title: p('kpi_contributable'), value: stats.cotisables, icon: TrendingUp, color: 'from-purple-500 to-purple-600' },
          { title: p('kpi_taxable'), value: stats.imposables, icon: CheckCircle, color: 'from-orange-500 to-orange-600' },
          { title: p('kpi_total_items'), value: stats.totalItems, icon: FileText, color: 'from-pink-500 to-pink-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div 
            key={i} 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: i * 0.1 }} 
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Primes / Indemnités Toggle Tabs - MOVED BELOW CARDS */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20 shadow-xl shadow-black/10">
        <div className="flex space-x-1">
          <button
            type="button"
            onClick={() => { setActiveTab('primes'); clearSelection(); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'primes'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <DollarSign className="h-5 w-5" />
            <span>{p('tab_bonuses')}</span>
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab('indemnites'); clearSelection(); }}
            className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'indemnites'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <DollarSign className="h-5 w-5" />
            <span>{p('tab_allowances')}</span>
          </button>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="glass-panel glass-table-shell overflow-visible">
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                />
                {showCol('code') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_code')}</th> : null}
                {showCol('designation') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_designation')}</th> : null}
                {showCol('type') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_type')}</th> : null}
                {showCol('default_value') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_default_value')}</th> : null}
                {showCol('ceiling') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_ceiling')}</th> : null}
                {showCol('contributable') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_contributable')}</th> : null}
                {showCol('taxable') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_taxable')}</th> : null}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumns}
                  onToggle={handleToggleColumn}
                  visibleCount={visibleColumnCount}
                  totalCount={qawaemColumns.length}
                  thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {loading ? (
                <tr>
                  <td colSpan={visibleTableColCount} className="text-center py-12">
                    <ArrowPathIcon className="h-12 w-12 text-white/30 mx-auto mb-3 animate-spin" />
                    <p className="text-white/50">{p('loading')}</p>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={visibleTableColCount} className="text-center py-12">
                    <InformationCircleIcon className="h-12 w-12 text-white/30 mx-auto mb-3" />
                    <p className="text-white/50 text-lg">{p('empty')}</p>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(item.id) ? 'bg-white/10' : ''}`}
                  >
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(item.id)}
                      onChange={() => handleSelectRecord(item.id)}
                    />
                    {showCol('code') ? (
                    <td className="px-6 py-4">
                      <span className="text-white font-semibold font-mono text-sm">{item.code}</span>
                    </td>
                    ) : null}
                    {showCol('designation') ? (
                    <td className="px-6 py-4">
                      <span className="text-white font-semibold">{item.designation}</span>
                    </td>
                    ) : null}
                    {showCol('type') ? (
                    <td className="px-6 py-4">
                      <span className="text-white/70 text-sm">
                        {item.type === 'pourcentage' ? p('type_percentage') : p('type_fixed')}
                      </span>
                    </td>
                    ) : null}
                    {showCol('default_value') ? (
                    <td className="px-6 py-4">
                      <span className="text-white font-bold text-lg">
                        {item.type === 'pourcentage'
                          ? `${item.valeurDefaut}%`
                          : formatBrandMoney(item.valeurDefaut)}
                      </span>
                    </td>
                    ) : null}
                    {showCol('ceiling') ? (
                    <td className="px-6 py-4">
                      <span className="text-white/70 text-sm">
                        {item.type === 'pourcentage' 
                          ? item.plafond ? `${item.plafond}%` : p('ceiling_none')
                          : p('ceiling_na')
                        }
                      </span>
                    </td>
                    ) : null}
                    {showCol('contributable') ? (
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                        item.cotisable 
                          ? 'bg-green-500/20 text-green-400 border-green-500/30'
                          : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }`}>
                        {item.cotisable ? p('yes') : p('no')}
                      </span>
                    </td>
                    ) : null}
                    {showCol('taxable') ? (
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                        item.imposable 
                          ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                          : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }`}>
                        {item.imposable ? p('yes') : p('no')}
                      </span>
                    </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onEdit={() => handleEdit(item)}
                        onDelete={() => handleDeleteItem(item.id)}
                        editTitle={p('edit')}
                        deleteTitle={p('delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-white/5 border-t border-white/10">
                <td colSpan={visibleTableColCount} className="px-6 py-4">
                  <div className="flex items-center justify-between text-white/70 text-sm">
                    <span>{activeTab === 'primes' ? p('footer_total_bonuses', { count: filteredItems.length }) : p('footer_total_allowances', { count: filteredItems.length })}</span>
                    <span>{p('footer_contributable')} <span className="text-white font-semibold">{filteredItems.filter(item => item.cotisable).length}</span></span>
                    <span>{p('footer_taxable')} <span className="text-white font-semibold">{filteredItems.filter(item => item.imposable).length}</span></span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
        
      {/* Note d'information */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-200">
            {activeTab === 'primes' ? (
              <>
                {p('info_bonus_p1')}
                <br />
                {p('info_bonus_p2')}
              </>
            ) : (
              <>
                {p('info_allowance_p1')}
                <br />
                {p('info_allowance_p2')}
              </>
            )}
          </p>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">
                {editingRecord
                  ? (activeTab === 'primes' ? p('modal_edit_bonus') : p('modal_edit_allowance'))
                  : (activeTab === 'primes' ? p('modal_new_bonus') : p('modal_new_allowance'))}
              </h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="prime-indemnite-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? p('update') : p('create')}
                </button>
                <button type="button" onClick={resetForm} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="prime-indemnite-form" onSubmit={handleSubmit}>
                <div className={SALAIRES_FORM_GRID_CLASS}>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      <RequiredLabelText text={p('form_code')} required />
                    </label>
                    <input type="text" required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_code')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      <RequiredLabelText text={p('form_designation')} required />
                    </label>
                    <input type="text" required value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_designation')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      <RequiredLabelText text={p('form_type')} required />
                    </label>
                    <CustomSelect value={formData.type} onChange={(value) => setFormData({ ...formData, type: value })} options={[{ value: 'pourcentage', label: p('form_type_percentage') }, { value: 'fixe', label: p('form_type_fixed') }]} placeholder={p('form_select')} autoSelect={false} searchable={true} creatable={true} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                      <RequiredLabelText text={formData.type === 'pourcentage' ? p('form_default_value_pct') : p('form_default_value_sar')} required />
                    </label>
                    <CustomNumberInput required step={formData.type === 'pourcentage' ? 0.01 : 1} decimals={formData.type === 'pourcentage' ? 2 : 0} value={formData.valeurDefaut} onChange={(v) => setFormData({ ...formData, valeurDefaut: v })} placeholder={formData.type === 'pourcentage' ? '25' : '750'} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  </div>
                  {formData.type === 'pourcentage' && (
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_ceiling')} <span className="text-white/40 text-xs">{p('optional')}</span></label>
                      <CustomNumberInput step={0.01} decimals={2} value={formData.plafond} onChange={(v) => setFormData({ ...formData, plafond: v })} placeholder="20" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                    </div>
                  )}
                  <div className="min-w-0 flex items-end gap-6 pb-1">
                    <div className="flex items-center">
                      <input type="checkbox" id="cotisable" checked={formData.cotisable} onChange={(e) => setFormData({ ...formData, cotisable: e.target.checked })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                      <label htmlFor="cotisable" className="ml-2 block text-sm text-white">{p('form_contributable')}</label>
                    </div>
                    <div className="flex items-center">
                      <input type="checkbox" id="imposable" checked={formData.imposable} onChange={(e) => setFormData({ ...formData, imposable: e.target.checked })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                      <label htmlFor="imposable" className="ml-2 block text-sm text-white">{p('form_taxable')}</label>
                    </div>
                  </div>
                  <div className="md:col-span-3 min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('form_description')} <span className="text-white/40 text-xs">{p('optional')}</span></label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" rows="3" placeholder={p('placeholder_description')} />
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default PrimesIndemnitesPage;


